import React, { useState } from "react";
import { getSortedFields, getFieldDisplayName, searchFields } from "../utils/fieldMapping";

const AggregationBuilder = ({ fieldMetadata, aggregations, onChange }) => {
  const [newAggregation, setNewAggregation] = useState({
    field: '',
    type: 'count',
    outputField: '',
    label: '',
    stageName: '' // Для временных агрегаций по стадиям
  });

  // Доступные стадии таймлайна
  const timelineStages = [
    { value: 'Резка стекла', label: 'Резка стекла' },
    { value: 'Обработка стекла', label: 'Обработка стекла' },
    { value: 'Готово к закалке', label: 'Готово к закалке' },
    { value: 'Стекло на закалке', label: 'Стекло на закалке' },
    { value: 'Матировка', label: 'Матировка' },
    { value: 'Упаковка', label: 'Упаковка' },
    { value: 'Комплектация', label: 'Комплектация' },
    { value: 'Готово к доставке', label: 'Готово к доставке' }
  ];

  // Типы агрегации
  const aggregationTypes = [
    { value: 'count', label: 'Количество записей', description: 'Подсчитывает количество записей в группе', category: 'basic' },
    { value: 'sum', label: 'Сумма', description: 'Суммирует числовые значения', category: 'basic' },
    { value: 'avg', label: 'Среднее значение', description: 'Вычисляет среднее арифметическое', category: 'basic' },
    { value: 'min', label: 'Минимальное значение', description: 'Находит наименьшее значение', category: 'basic' },
    { value: 'max', label: 'Максимальное значение', description: 'Находит наибольшее значение', category: 'basic' },
    { value: 'unique', label: 'Уникальные значения', description: 'Подсчитывает количество уникальных значений', category: 'basic' },

    // Временные агрегации для анализа таймлайна
    { value: 'timeline_avg_total', label: 'Среднее общее время', description: 'Среднее время прохождения всех стадий (часы)', category: 'timeline' },
    { value: 'timeline_min_total', label: 'Минимальное общее время', description: 'Самое быстрое прохождение (часы)', category: 'timeline' },
    { value: 'timeline_max_total', label: 'Максимальное общее время', description: 'Самое долгое прохождение (часы)', category: 'timeline' },
    { value: 'timeline_avg_stage', label: 'Среднее время на стадии', description: 'Среднее время на конкретной стадии (часы)', category: 'timeline', requiresStage: true },
    { value: 'timeline_min_stage', label: 'Минимальное время на стадии', description: 'Самое быстрое прохождение стадии (часы)', category: 'timeline', requiresStage: true },
    { value: 'timeline_max_stage', label: 'Максимальное время на стадии', description: 'Самое долгое прохождение стадии (часы)', category: 'timeline', requiresStage: true }
  ];

  // Расширенный список fallback полей для агрегации
  const fallbackFields = [
    // Основные поля
    { value: 'id', label: 'ID', type: 'number' },
    { value: 'opportunity', label: 'Сумма', type: 'number' },
    { value: 'assignedById', label: 'Ответственный', type: 'number' },
    { value: 'createdBy', label: 'Кто создал', type: 'number' },
    { value: 'companyId', label: 'Компания', type: 'number' },
    { value: 'contactId', label: 'Контакт', type: 'number' },

    // Строковые поля для подсчета уникальных
    { value: 'title', label: 'Название', type: 'string' },
    { value: 'stageId', label: 'Стадия', type: 'string' },
    { value: 'ufCrm3_1746789101', label: 'Номер заказа', type: 'string' },
    { value: 'ufCrm3_1746789474', label: 'ID-Франчайзи', type: 'string' },

    // Числовые поля для всех операций
    { value: 'ufCrm3_1746789589', label: 'Количество', type: 'number' },
    { value: 'ufCrm3_1746789599', label: 'Цена за единицу', type: 'number' },
    { value: 'ufCrm3_1746789627', label: 'Общая стоимость', type: 'number' },

    // Временные поля
    { value: 'createdTime', label: 'Дата создания', type: 'date' },
    { value: 'updatedTime', label: 'Дата изменения', type: 'date' },
    { value: 'ufCrm3_1746789890', label: 'Дата доставки', type: 'date' }
  ];

  // Получение доступных полей
  const getAvailableFields = () => {
    console.log('AggregationBuilder - fieldMetadata:', fieldMetadata);
    console.log('AggregationBuilder - fieldMetadata keys count:', Object.keys(fieldMetadata || {}).length);

    // Получаем все поля из маппинга
    const mappedFields = getSortedFields().map(field => ({
      value: field.id,
      label: field.name,
      type: getFieldTypeFromMapping(field.id)
    }));

    console.log('AggregationBuilder - поля из маппинга:', mappedFields.length);
    return mappedFields;
  };

  // Определяем тип поля на основе его ID
  const getFieldTypeFromMapping = (fieldId) => {
    const dateFields = [
      'createdTime', 'updatedTime',
      'ufCrm3_1749213244986', 'ufCrm3_1749213350972', 'ufCrm3_1749555592378'
    ];

    const numberFields = [
      'id', 'assignedById', 'opportunity', 'companyId', 'contactId', 'createdBy'
    ];

    if (dateFields.includes(fieldId)) return 'date';
    if (numberFields.includes(fieldId)) return 'number';
    return 'string';
  };

  // Получение подходящих типов агрегации для поля
  const getAvailableAggregationTypes = (fieldKey) => {
    const fieldType = getFieldTypeFromMapping(fieldKey);

    if (fieldType === 'number') {
      return aggregationTypes; // Все типы доступны для числовых полей
    } else {
      return aggregationTypes.filter(type =>
        ['count', 'unique'].includes(type.value)
      ); // Только подсчет для нечисловых полей
    }
  };

  // Автоматическое создание названия поля и подписи
  const generateFieldNames = (field, type, stageName = '') => {
    const typeObj = aggregationTypes.find(t => t.value === type);
    if (!typeObj) return { outputField: '', label: '' };

    let outputField = '';
    let label = '';

    // Для временных агрегаций
    if (type.startsWith('timeline_')) {
      if (type.includes('_stage') && stageName) {
        const stageSlug = stageName.toLowerCase().replace(/\s+/g, '_');
        outputField = `${type}_${stageSlug}`;
        label = `${typeObj.label} (${stageName})`;
      } else if (type.includes('_total')) {
        outputField = type;
        label = typeObj.label;
      } else {
        outputField = type;
        label = typeObj.label;
      }
    } else {
      // Для обычных агрегаций
      const fieldObj = getAvailableFields().find(f => f.value === field);
      if (!fieldObj) return { outputField: '', label: '' };

      outputField = `${field}_${type}`;

      switch (type) {
        case 'count':
          label = `Количество записей`;
          break;
        case 'sum':
          label = `Сумма ${fieldObj.label}`;
          break;
        case 'avg':
          label = `Среднее ${fieldObj.label}`;
          break;
        case 'min':
          label = `Минимум ${fieldObj.label}`;
          break;
        case 'max':
          label = `Максимум ${fieldObj.label}`;
          break;
        case 'unique':
          label = `Уникальных ${fieldObj.label}`;
          break;
      }
    }

    return { outputField, label };
  };

  // Добавление новой агрегации
  const addAggregation = () => {
    const typeObj = aggregationTypes.find(t => t.value === newAggregation.type);

    // Валидация для обычных агрегаций
    if (!newAggregation.type.startsWith('timeline_') && !newAggregation.field && newAggregation.type !== 'count') {
      alert('Пожалуйста, выберите поле для агрегации');
      return;
    }

    // Валидация для временных агрегаций по стадиям
    if (typeObj?.requiresStage && !newAggregation.stageName) {
      alert('Пожалуйста, выберите стадию для анализа');
      return;
    }

    if (!newAggregation.label) {
      alert('Пожалуйста, укажите название для счетчика');
      return;
    }

    const aggregationToAdd = {
      id: Date.now(),
      ...newAggregation,
      outputField: newAggregation.outputField || `${newAggregation.field || 'total'}_${newAggregation.type}`
    };

    onChange([...aggregations, aggregationToAdd]);

    // Сброс формы
    setNewAggregation({
      field: '',
      type: 'count',
      outputField: '',
      label: '',
      stageName: ''
    });
  };

  // Удаление агрегации
  const removeAggregation = (aggregationId) => {
    onChange(aggregations.filter(a => a.id !== aggregationId));
  };

  // Обновление агрегации
  const updateAggregation = (aggregationId, updates) => {
    onChange(aggregations.map(a => a.id === aggregationId ? { ...a, ...updates } : a));
  };

  // Получение описания агрегации
  const getAggregationDescription = (aggregation) => {
    const typeObj = aggregationTypes.find(t => t.value === aggregation.type);
    const fieldObj = getAvailableFields().find(f => f.value === aggregation.field);

    let description = aggregation.label || typeObj?.label || '';

    if (fieldObj && aggregation.type !== 'count') {
      description += ` (${fieldObj.label})`;
    }

    return description;
  };

  // Обработка изменения поля или типа агрегации
  const handleFieldOrTypeChange = (field, type) => {
    const { outputField, label } = generateFieldNames(field, type, newAggregation.stageName);
    setNewAggregation({
      ...newAggregation,
      field,
      type,
      outputField,
      label
    });
  };

  // Обработка изменения стадии для временных агрегаций
  const handleStageChange = (stageName) => {
    const { outputField, label } = generateFieldNames(newAggregation.field, newAggregation.type, stageName);
    setNewAggregation({
      ...newAggregation,
      stageName,
      outputField,
      label
    });
  };

  const availableFields = getAvailableFields();

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Настройка счетчиков и агрегации</h3>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start space-x-2">
          <div>
            <h4 className="text-purple-900 font-medium">Счетчики данных</h4>
            <p className="text-purple-700 text-sm mt-1">
              Добавьте вычисления для каждой группы: количество заказов, общая сумма, средний чек и другие метрики.
              Эти данные будут автоматически рассчитаны для каждой группы.
            </p>
          </div>
        </div>
      </div>

      {/* Список существующих агрегаций */}
      {aggregations.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Активные счетчики ({aggregations.length})</h4>
          {aggregations.map((aggregation) => (
            <div key={aggregation.id} className="bg-gray-50 border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {getAggregationDescription(aggregation)}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Поле результата: {aggregation.outputField}
                  </div>
                </div>
                <button
                  onClick={() => removeAggregation(aggregation.id)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Удалить
                </button>
              </div>

              {/* Быстрое редактирование */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Название</label>
                  <input
                    type="text"
                    value={aggregation.label}
                    onChange={(e) => updateAggregation(aggregation.id, { label: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Поле результата</label>
                  <input
                    type="text"
                    value={aggregation.outputField}
                    onChange={(e) => updateAggregation(aggregation.id, { outputField: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Форма добавления новой агрегации */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <h4 className="font-medium text-gray-900 mb-4">Добавить счетчик</h4>

        <div className="space-y-4">
          {/* Первая строка: Тип агрегации */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Тип вычисления
            </label>

            {/* Базовые агрегации */}
            <div className="mb-4">
              <h5 className="text-xs font-medium text-gray-600 mb-2">📊 Базовые вычисления</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {aggregationTypes.filter(t => t.category === 'basic').map((type) => (
                  <label key={type.value} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="radio"
                      name="aggregationType"
                      value={type.value}
                      checked={newAggregation.type === type.value}
                      onChange={(e) => handleFieldOrTypeChange(newAggregation.field, e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{type.label}</div>
                      <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Временные агрегации */}
            <div>
              <h5 className="text-xs font-medium text-gray-600 mb-2">⏱️ Анализ времени прохождения стадий</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {aggregationTypes.filter(t => t.category === 'timeline').map((type) => (
                  <label key={type.value} className="flex items-start space-x-3 p-3 border border-purple-200 rounded-lg hover:bg-purple-50 cursor-pointer">
                    <input
                      type="radio"
                      name="aggregationType"
                      value={type.value}
                      checked={newAggregation.type === type.value}
                      onChange={(e) => handleFieldOrTypeChange(newAggregation.field, e.target.value)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-purple-900">{type.label}</div>
                      <div className="text-xs text-purple-700 mt-1">{type.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Выбор стадии для временных агрегаций */}
          {aggregationTypes.find(t => t.value === newAggregation.type)?.requiresStage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Стадия производства *
              </label>
              <select
                value={newAggregation.stageName}
                onChange={(e) => handleStageChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Выберите стадию</option>
                {timelineStages.map((stage) => (
                  <option key={stage.value} value={stage.value}>
                    {stage.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Вторая строка: Поле (только если не count и не timeline) */}
          {!newAggregation.type.startsWith('timeline_') && newAggregation.type !== 'count' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Поле для вычисления *
              </label>
              <select
                value={newAggregation.field}
                onChange={(e) => handleFieldOrTypeChange(e.target.value, newAggregation.type)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Выберите поле</option>
                {availableFields
                  .filter(field => {
                    // Для числовых агрегаций показываем только числовые поля
                    if (['sum', 'avg', 'min', 'max'].includes(newAggregation.type)) {
                      return field.type === 'number';
                    }
                    return true;
                  })
                  .map((field) => (
                    <option key={field.value} value={field.value}>
                      {field.label} {field.type === 'number' ? '(число)' : ''}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Третья строка: Настройки результата */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Название счетчика *
              </label>
              <input
                type="text"
                value={newAggregation.label}
                onChange={(e) => setNewAggregation({...newAggregation, label: e.target.value})}
                placeholder="Например: Количество заказов"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Поле результата
              </label>
              <input
                type="text"
                value={newAggregation.outputField}
                onChange={(e) => setNewAggregation({...newAggregation, outputField: e.target.value})}
                placeholder="order_count"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="text-xs text-gray-500 mt-1">
                Техническое название поля для результата (автоматически)
              </div>
            </div>
          </div>

          {/* Предварительный просмотр */}
          {newAggregation.type && newAggregation.label && (
            <div className={`border rounded-lg p-3 ${newAggregation.type.startsWith('timeline_') ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}>
              <div className={`text-sm font-medium ${newAggregation.type.startsWith('timeline_') ? 'text-purple-900' : 'text-blue-900'}`}>
                <strong>Предварительный просмотр:</strong>
              </div>
              <div className={`text-sm mt-1 ${newAggregation.type.startsWith('timeline_') ? 'text-purple-700' : 'text-blue-700'}`}>
                В каждой группе будет добавлено поле "{newAggregation.outputField}" со значением "{newAggregation.label}"
              </div>
              {newAggregation.type === 'count' && (
                <div className="text-xs text-blue-600 mt-1">
                  Например: Группа "АЛМА" → order_count: 25 (25 заказов в группе)
                </div>
              )}
              {newAggregation.type === 'sum' && newAggregation.field && (
                <div className="text-xs text-blue-600 mt-1">
                  Например: Группа "АЛМА" → total_sum: 150000 (сумма всех заказов в группе)
                </div>
              )}
              {newAggregation.type === 'timeline_avg_total' && (
                <div className="text-xs text-purple-600 mt-1">
                  Например: Группа "АЛМА" → timeline_avg_total: 48.50 (средний срок 48.5 часов)
                </div>
              )}
              {newAggregation.type.includes('_stage') && newAggregation.stageName && (
                <div className="text-xs text-purple-600 mt-1">
                  Например: Группа "АЛМА" → {newAggregation.outputField}: 12.30 (среднее время на стадии {newAggregation.stageName}: 12.3 часа)
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <button
              onClick={addAggregation}
              disabled={
                (!newAggregation.type.startsWith('timeline_') && !newAggregation.field && newAggregation.type !== 'count') ||
                (aggregationTypes.find(t => t.value === newAggregation.type)?.requiresStage && !newAggregation.stageName) ||
                !newAggregation.label
              }
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              + Добавить счетчик
            </button>
          </div>
        </div>
      </div>

      {/* Примеры использования */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-yellow-900 font-medium mb-2">💡 Примеры счетчиков</h4>
        <div className="text-yellow-800 text-sm space-y-1">
          <div><strong>Количество заказов:</strong> Тип: Количество записей → Результат: сколько заказов в каждой группе</div>
          <div><strong>Общая сумма продаж:</strong> Поле: Сумма, Тип: Сумма → Результат: общая сумма заказов в группе</div>
          <div><strong>Средний чек:</strong> Поле: Сумма, Тип: Среднее → Результат: средняя сумма заказа в группе</div>
          <div><strong>Максимальный заказ:</strong> Поле: Сумма, Тип: Максимум → Результат: самый дорогой заказ в группе</div>
          <div><strong>Уникальные клиенты:</strong> Поле: ID клиента, Тип: Уникальные → Результат: количество разных клиентов</div>
          <div className="mt-2 pt-2 border-t border-yellow-300">
            <strong>⏱️ Временные метрики:</strong>
          </div>
          <div><strong>Средний срок производства:</strong> Тип: Среднее общее время → Результат: среднее время от первой до последней стадии</div>
          <div><strong>Время на резке стекла:</strong> Тип: Среднее время на стадии, Стадия: Резка стекла → Результат: среднее время на стадии резки</div>
          <div><strong>Самый быстрый заказ:</strong> Тип: Минимальное общее время → Результат: самое быстрое прохождение всех стадий</div>
        </div>
      </div>
    </div>
  );
};

export default AggregationBuilder;